import {
  ARIBucketConfig,
  BundleRateType,
  StaticBundleData,
} from "../../../../gen/witbooking/ari/v1/static_ari_pb";
import { InventoryLine } from "../../../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { StaticBundleDataWithName } from "../../hooks";
import { BackofficeARIDTO, DailySet, ValueTypes } from "../types";
import { InventoryConfiguration } from "./InventoryConfiguration";

interface DailyValueDimension {
  dailySet?: DailySet[];
  parentTicker?: string;
}

interface DailyValues {
  rate?: DailyValueDimension;
  minStay?: DailyValueDimension;
  maxStay?: DailyValueDimension;
  minNotice?: DailyValueDimension;
  maxNotice?: DailyValueDimension;
  closeToArrival?: DailyValueDimension;
  closeToDeparture?: DailyValueDimension;
}

interface GetInventoryNameRequest {
  ticker: string;
  name?: string;
  accommodation?: string;
  configuration?: string;
  mealPlan?: string;
  condition?: string;
}

type AriElement = {
  [key in ValueTypes]: {
    dailySet: DailySet[];
  };
};

type AriBucketConfigType = keyof ARIBucketConfig.AsObject;

const hasOwnConfig = (configType: AriBucketConfigType, bucketConfig?: ARIBucketConfig.AsObject) => {
  const config = bucketConfig?.[configType];
  return config && !config.parentTicker && !config.constant && !config["parentTickers"];
};

export const hasSomeOwnConfig = (bundle: StaticBundleData.AsObject) => {
  return (
    bundle.bundleRateType === BundleRateType.BUNDLE_TYPE_OWN ||
    (bundle.ticker === bundle.bundleTicker && // owned restrictions only for bundles not for bundle elements
      (hasOwnConfig("minStay", bundle.bucketConfig) ||
        hasOwnConfig("maxStay", bundle.bucketConfig) ||
        hasOwnConfig("minNotice", bundle.bucketConfig) ||
        hasOwnConfig("maxNotice", bundle.bucketConfig) ||
        hasOwnConfig("closeToArrival", bundle.bucketConfig) ||
        hasOwnConfig("closeToDeparture", bundle.bucketConfig)))
  );
};

const getDailyValueDimension = (
  configType: AriBucketConfigType,
  startDate: string,
  endDate: string,
  bucketConfig?: ARIBucketConfig.AsObject,
  ariElement?: AriElement
): DailyValueDimension => {
  return {
    dailySet: ariElement?.[configType]?.dailySet || [{ startDate, endDate, value: 0 }],
    parentTicker: bucketConfig?.rate?.parentTicker,
  };
};

const getDailyValue = (
  startDate: string,
  endDate: string,
  bucketConfig?: ARIBucketConfig.AsObject,
  ariElement?: AriElement
): DailyValues => {
  const keys = [
    "rate",
    "minStay",
    "maxStay",
    "minNotice",
    "maxNotice",
    "closeToArrival",
    "closeToDeparture",
  ] as const;

  const result: DailyValues = {};
  for (const key of keys) {
    if (hasOwnConfig(key, bucketConfig)) {
      result[key] = getDailyValueDimension(key, startDate, endDate, bucketConfig, ariElement);
    }
  }
  return result;
};

const getInventoryName = ({
  name,
  ticker,
  accommodation,
  configuration,
  mealPlan,
  condition,
}: GetInventoryNameRequest): string => {
  if (!accommodation || !configuration || !mealPlan || !condition) {
    return name ? `${name} (${ticker})` : ticker;
  }
  return name
    ? `${name} - ${accommodation}, ${configuration}, ${mealPlan}, ${condition} (${ticker})`
    : `${accommodation}, ${configuration}, ${mealPlan}, ${condition} (${ticker})`;
};

export class BundleInventoryData {
  ticker: string;
  dailyValues: DailyValues;
  name: string;
  configuration: InventoryConfiguration;

  private constructor(
    ticker: string,
    dailyValues: DailyValues,
    name: string,
    configuration: InventoryConfiguration
  ) {
    this.ticker = ticker;
    this.dailyValues = dailyValues;
    this.name = name;
    this.configuration = configuration;
  }

  static of(
    backofficeAriDto: BackofficeARIDTO,
    bundleInventoryList: StaticBundleDataWithName[],
    inventoryLinesList: InventoryLine.AsObject[],
    startDate: string,
    endDate: string
  ): BundleInventoryData[] {
    const bundlesByTicker = bundleInventoryList.reduce(
      (acc, bundle) => ({
        ...acc,
        [bundle.ticker]: bundle,
      }),
      {} as Record<string, StaticBundleDataWithName>
    );

    return bundleInventoryList
      .filter(bundle => hasSomeOwnConfig(bundle))
      .map(bundle => {
        const parentBundle = bundlesByTicker[bundle.bundleTicker];
        const inventoryLine = inventoryLinesList.find(
          it => it.code === bundle.ticker.replace(`${bundle.bundleTicker}_`, "")
        );

        return new BundleInventoryData(
          bundle.ticker,
          getDailyValue(startDate, endDate, bundle.bucketConfig, backofficeAriDto[bundle.ticker]),
          getInventoryName({
            name: parentBundle?.name,
            ticker: bundle.ticker,
            accommodation: inventoryLine?.accommodation?.name,
            configuration: inventoryLine?.configuration?.name,
            mealPlan: inventoryLine?.mealPlan?.name,
            condition: inventoryLine?.condition?.name,
          }),
          {
            accommodation: inventoryLine?.accommodation?.ticker || "",
            condition: inventoryLine?.condition?.ticker || "",
            mealPlan: inventoryLine?.mealPlan?.ticker || "",
            occupancy: inventoryLine?.configuration?.ticker || "",
          }
        );
      });
  }
}
